// Modèle de données de l'espace Agence (frontend uniquement, aucune donnée fictive).
// Relations : propriétaire → immeuble → bien → contrat → locataire.

export type AgenceStatutContrat = "brouillon" | "actif" | "expire" | "resilie" | "suspendu";
export type AgenceStatutLoyer = "paye" | "a-venir" | "en-retard" | "impaye";

/* ------------------------------------------------------------------ */
/* Immeuble                                                            */
/* ------------------------------------------------------------------ */

export interface AgenceImmeuble {
  id: string;
  proprietaireId: string;
  nom: string;
  description: string;
  photo: string;
  /** Identifiants territoriaux (FK Supabase : regions/provinces/communes/secteurs). */
  regionId: string;
  provinceId: string;
  communeId: string;
  secteurId: string;
  /** Libellés territoriaux résolus pour l'affichage (non utilisés comme FK). */
  region: string;
  province: string;
  commune: string;
  secteur: string;
  adresse: string;
  niveaux: number;
  infosComplementaires: string;
  createdAt: string;
}

export type AgenceImmeubleInput = Omit<AgenceImmeuble, "id" | "createdAt">;

/* ------------------------------------------------------------------ */
/* Bien                                                                */
/* ------------------------------------------------------------------ */

export type AgenceBienType =
  | "appartement"
  | "studio"
  | "chambre"
  | "boutique"
  | "bureau"
  | "magasin"
  | "local-commercial"
  | "autre";

export type AgenceBienStatut = "disponible" | "occupe" | "reserve" | "maintenance" | "indisponible";

export interface AgenceBien {
  id: string;
  proprietaireId: string;
  immeubleId: string;
  type: AgenceBienType;
  reference: string;
  etage: string;
  numero: string;
  surface: number;
  loyerMensuel: number;
  statut: AgenceBienStatut;
  description: string;
  infosComplementaires: string;
  createdAt: string;
}

export type AgenceBienInput = Omit<AgenceBien, "id" | "createdAt">;

export const AGENCE_BIEN_TYPE_LABELS: Record<AgenceBienType, string> = {
  appartement: "Appartement",
  studio: "Studio",
  chambre: "Chambre",
  boutique: "Boutique",
  bureau: "Bureau",
  magasin: "Magasin",
  "local-commercial": "Local commercial",
  autre: "Autre",
};

export const AGENCE_BIEN_TYPE_ICONS: Record<AgenceBienType, string> = {
  appartement: "ri-home-4-line",
  studio: "ri-home-5-line",
  chambre: "ri-hotel-line",
  boutique: "ri-store-line",
  bureau: "ri-briefcase-line",
  magasin: "ri-store-2-line",
  "local-commercial": "ri-building-4-line",
  autre: "ri-home-4-line",
};

export const AGENCE_BIEN_TYPE_OPTIONS = (Object.keys(AGENCE_BIEN_TYPE_LABELS) as AgenceBienType[]).map(
  (value) => ({ value, label: AGENCE_BIEN_TYPE_LABELS[value] })
);

export const AGENCE_BIEN_STATUT_LABELS: Record<AgenceBienStatut, string> = {
  disponible: "Disponible",
  occupe: "Occupé",
  reserve: "Réservé",
  maintenance: "En maintenance",
  indisponible: "Indisponible",
};

export const AGENCE_BIEN_STATUT_TONES: Record<
  AgenceBienStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  disponible: "secondary",
  occupe: "primary",
  reserve: "accent",
  maintenance: "neutral",
  indisponible: "danger",
};

export const AGENCE_BIEN_STATUT_OPTIONS = (Object.keys(AGENCE_BIEN_STATUT_LABELS) as AgenceBienStatut[]).map(
  (value) => ({ value, label: AGENCE_BIEN_STATUT_LABELS[value] })
);

/* ------------------------------------------------------------------ */
/* Locataire (créé indépendamment d'un bien)                           */
/* ------------------------------------------------------------------ */

export type AgenceTypePiece = "cni" | "passeport" | "permis" | "carte-consulaire" | "autre";

export interface AgenceLocataire {
  id: string;
  prenom: string;
  nom: string;
  telephone: string;
  /** Numéro WhatsApp (facultatif) — prépare les futures relances WhatsApp. */
  whatsapp: string;
  email: string;
  adresse: string;
  profession: string;
  typePiece: AgenceTypePiece | "";
  numeroPiece: string;
  contactUrgence: string;
  notes: string;
  /**
   * Rattachement interne (FK Supabase `proprietaire_id`).
   * Préparé pour le prochain bloc : la valeur ne peut pas encore être
   * déterminée tant que le CRUD Supabase (propriétaires gérés) n'est pas branché.
   */
  proprietaireId?: string;
  /**
   * Rattachement interne (FK Supabase `agence_id`).
   * Préparé pour le prochain bloc : la valeur ne peut pas encore être
   * déterminée tant que le CRUD Supabase (agence) n'est pas branché.
   */
  agenceId?: string;
  createdAt: string;
}

export type AgenceLocataireInput = Omit<AgenceLocataire, "id" | "createdAt">;

export const AGENCE_TYPE_PIECE_LABELS: Record<AgenceTypePiece, string> = {
  cni: "CNI",
  passeport: "Passeport",
  permis: "Permis de conduire",
  "carte-consulaire": "Carte consulaire",
  autre: "Autre",
};

export const AGENCE_TYPE_PIECE_OPTIONS = (Object.keys(AGENCE_TYPE_PIECE_LABELS) as AgenceTypePiece[]).map(
  (value) => ({ value, label: AGENCE_TYPE_PIECE_LABELS[value] })
);

export function agenceLocataireNomComplet(locataire: AgenceLocataire): string {
  return [locataire.prenom, locataire.nom].filter(Boolean).join(" ").trim() || "Locataire sans nom";
}

export function agenceLocataireInitiales(locataire: AgenceLocataire): string {
  const prenom = locataire.prenom.trim().charAt(0);
  const nom = locataire.nom.trim().charAt(0);
  return `${prenom}${nom}`.toUpperCase() || "L";
}

/* ------------------------------------------------------------------ */
/* Contrat                                                             */
/* ------------------------------------------------------------------ */

export interface AgenceContrat {
  id: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  dateDebut: string;
  dateFin: string;
  loyerMensuel: number;
  caution: number;
  avance: number;
  jourEcheance: number;
  conditionsParticulieres: string;
  document: string;
  statut: AgenceStatutContrat;
  createdAt: string;
}

export type AgenceContratInput = Omit<AgenceContrat, "id" | "createdAt">;

export const AGENCE_CONTRAT_STATUT_LABELS: Record<AgenceStatutContrat, string> = {
  brouillon: "Brouillon",
  actif: "Actif",
  expire: "Expiré",
  resilie: "Résilié",
  suspendu: "Suspendu",
};

export const AGENCE_CONTRAT_STATUT_TONES: Record<
  AgenceStatutContrat,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  brouillon: "neutral",
  actif: "secondary",
  expire: "accent",
  resilie: "danger",
  suspendu: "accent",
};

export const AGENCE_CONTRAT_STATUT_OPTIONS = (Object.keys(AGENCE_CONTRAT_STATUT_LABELS) as AgenceStatutContrat[]).map(
  (value) => ({ value, label: AGENCE_CONTRAT_STATUT_LABELS[value] })
);

export function agenceContratReference(id: string): string {
  return `CTR-${id.slice(-6).toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/* Loyer (calculé depuis les contrats actifs + les paiements réels)    */
/* ------------------------------------------------------------------ */

/** Au-delà de ce nombre de jours de retard, un loyer passe de « En retard » à « Impayé ». */
export const AGENCE_IMPAYE_SEUIL_JOURS = 30;

/**
 * Un loyer est une échéance mensuelle dérivée d'un contrat actif.
 * Il n'est jamais saisi directement : il est généré à partir du contrat
 * et son statut découle des paiements réellement enregistrés.
 */
export interface AgenceLoyer {
  id: string;
  contratId: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  /** Période au format `YYYY-MM`. */
  periode: string;
  montant: number;
  totalPaye: number;
  reste: number;
  /** Date d'échéance `YYYY-MM-DD`. */
  echeance: string;
  statut: AgenceStatutLoyer;
  joursRetard: number;
}

export const AGENCE_LOYER_STATUT_LABELS: Record<AgenceStatutLoyer, string> = {
  "a-venir": "À venir",
  paye: "Payé",
  "en-retard": "En retard",
  impaye: "Impayé",
};

export const AGENCE_LOYER_STATUT_TONES: Record<
  AgenceStatutLoyer,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  "a-venir": "neutral",
  paye: "secondary",
  "en-retard": "accent",
  impaye: "danger",
};

export const AGENCE_LOYER_STATUT_OPTIONS = (Object.keys(AGENCE_LOYER_STATUT_LABELS) as AgenceStatutLoyer[]).map(
  (value) => ({ value, label: AGENCE_LOYER_STATUT_LABELS[value] })
);

export function isAgenceImpaye(statut: AgenceStatutLoyer): boolean {
  return statut === "en-retard" || statut === "impaye";
}

/* ------------------------------------------------------------------ */
/* Paiement                                                            */
/* ------------------------------------------------------------------ */

export type AgenceModePaiement =
  | "especes"
  | "orange-money"
  | "moov-money"
  | "virement"
  | "cheque"
  | "autre";

export interface AgencePaiement {
  id: string;
  contratId: string;
  /** Période du loyer concerné, format `YYYY-MM`. */
  periode: string;
  montant: number;
  /** Date du paiement `YYYY-MM-DD`. */
  date: string;
  mode: AgenceModePaiement;
  reference: string;
  note: string;
  createdAt: string;
}

export type AgencePaiementInput = Omit<AgencePaiement, "id" | "createdAt">;

export const AGENCE_MODE_PAIEMENT_LABELS: Record<AgenceModePaiement, string> = {
  especes: "Espèces",
  "orange-money": "Orange Money",
  "moov-money": "Moov Money",
  virement: "Virement bancaire",
  cheque: "Chèque",
  autre: "Autre",
};

export const AGENCE_MODE_PAIEMENT_ICONS: Record<AgenceModePaiement, string> = {
  especes: "ri-money-cny-circle-line",
  "orange-money": "ri-smartphone-line",
  "moov-money": "ri-smartphone-line",
  virement: "ri-bank-line",
  cheque: "ri-file-paper-2-line",
  autre: "ri-more-line",
};

export const AGENCE_MODE_PAIEMENT_OPTIONS = (Object.keys(AGENCE_MODE_PAIEMENT_LABELS) as AgenceModePaiement[]).map(
  (value) => ({ value, label: AGENCE_MODE_PAIEMENT_LABELS[value] })
);

export function agencePaiementReference(id: string): string {
  return `PAY-${id.slice(-6).toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/* Dépense                                                             */
/* ------------------------------------------------------------------ */

export type AgenceDepenseCategorie =
  | "reparation"
  | "electricite"
  | "eau"
  | "nettoyage"
  | "securite"
  | "entretien"
  | "taxe"
  | "assurance"
  | "autre";

export interface AgenceDepense {
  id: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  libelle: string;
  categorie: AgenceDepenseCategorie;
  montant: number;
  date: string;
  description: string;
  justificatif: string;
  createdAt: string;
}

export type AgenceDepenseInput = Omit<AgenceDepense, "id" | "createdAt">;

export const AGENCE_DEPENSE_CATEGORIE_LABELS: Record<AgenceDepenseCategorie, string> = {
  reparation: "Réparation",
  electricite: "Électricité",
  eau: "Eau",
  nettoyage: "Nettoyage",
  securite: "Sécurité",
  entretien: "Entretien",
  taxe: "Taxe",
  assurance: "Assurance",
  autre: "Autre",
};

export const AGENCE_DEPENSE_CATEGORIE_ICONS: Record<AgenceDepenseCategorie, string> = {
  reparation: "ri-tools-line",
  electricite: "ri-flashlight-line",
  eau: "ri-water-flash-line",
  nettoyage: "ri-brush-line",
  securite: "ri-shield-check-line",
  entretien: "ri-hammer-line",
  taxe: "ri-government-line",
  assurance: "ri-shield-star-line",
  autre: "ri-more-line",
};

export const AGENCE_DEPENSE_CATEGORIE_TONES: Record<
  AgenceDepenseCategorie,
  "primary" | "accent" | "secondary" | "neutral"
> = {
  reparation: "accent",
  electricite: "secondary",
  eau: "primary",
  nettoyage: "neutral",
  securite: "secondary",
  entretien: "accent",
  taxe: "primary",
  assurance: "neutral",
  autre: "neutral",
};

export const AGENCE_DEPENSE_CATEGORIE_OPTIONS = (
  Object.keys(AGENCE_DEPENSE_CATEGORIE_LABELS) as AgenceDepenseCategorie[]
).map((value) => ({ value, label: AGENCE_DEPENSE_CATEGORIE_LABELS[value] }));

/* ------------------------------------------------------------------ */
/* Intervention de maintenance                                         */
/* ------------------------------------------------------------------ */

export type AgenceInterventionPriorite = "faible" | "normale" | "elevee" | "urgente";

export type AgenceInterventionStatut = "nouveau" | "en-cours" | "en-attente" | "termine" | "annule";

export interface AgenceIntervention {
  id: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  probleme: string;
  description: string;
  priorite: AgenceInterventionPriorite;
  statut: AgenceInterventionStatut;
  photos: string[];
  cout: number;
  prestataire: string;
  date: string;
  createdAt: string;
}

export type AgenceInterventionInput = Omit<AgenceIntervention, "id" | "createdAt">;

export const AGENCE_INTERVENTION_PRIORITE_LABELS: Record<AgenceInterventionPriorite, string> = {
  faible: "Faible",
  normale: "Normale",
  elevee: "Élevée",
  urgente: "Urgente",
};

export const AGENCE_INTERVENTION_PRIORITE_ICONS: Record<AgenceInterventionPriorite, string> = {
  faible: "ri-arrow-down-line",
  normale: "ri-subtract-line",
  elevee: "ri-arrow-up-line",
  urgente: "ri-alarm-warning-line",
};

export const AGENCE_INTERVENTION_PRIORITE_TONES: Record<
  AgenceInterventionPriorite,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  faible: "neutral",
  normale: "secondary",
  elevee: "accent",
  urgente: "danger",
};

export const AGENCE_INTERVENTION_PRIORITE_OPTIONS = (
  Object.keys(AGENCE_INTERVENTION_PRIORITE_LABELS) as AgenceInterventionPriorite[]
).map((value) => ({ value, label: AGENCE_INTERVENTION_PRIORITE_LABELS[value] }));

export const AGENCE_INTERVENTION_STATUT_LABELS: Record<AgenceInterventionStatut, string> = {
  nouveau: "Nouveau",
  "en-cours": "En cours",
  "en-attente": "En attente",
  termine: "Terminé",
  annule: "Annulé",
};

export const AGENCE_INTERVENTION_STATUT_ICONS: Record<AgenceInterventionStatut, string> = {
  nouveau: "ri-add-circle-line",
  "en-cours": "ri-loader-4-line",
  "en-attente": "ri-time-line",
  termine: "ri-checkbox-circle-line",
  annule: "ri-close-circle-line",
};

export const AGENCE_INTERVENTION_STATUT_TONES: Record<
  AgenceInterventionStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger"
> = {
  nouveau: "primary",
  "en-cours": "accent",
  "en-attente": "neutral",
  termine: "secondary",
  annule: "danger",
};

export const AGENCE_INTERVENTION_STATUT_OPTIONS = (
  Object.keys(AGENCE_INTERVENTION_STATUT_LABELS) as AgenceInterventionStatut[]
).map((value) => ({ value, label: AGENCE_INTERVENTION_STATUT_LABELS[value] }));

/* ------------------------------------------------------------------ */
/* Document                                                            */
/* ------------------------------------------------------------------ */

export type AgenceDocumentType =
  | "contrat"
  | "piece-identite"
  | "document-bien"
  | "recu"
  | "facture"
  | "autre";

export interface AgenceDocument {
  id: string;
  nom: string;
  type: AgenceDocumentType;
  date: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  contratId: string;
  locataireId: string;
  fichierUrl: string;
  fichierNom: string;
  fichierType: string;
  fichierTaille: number;
  createdAt: string;
}

export type AgenceDocumentInput = Omit<AgenceDocument, "id" | "createdAt">;

export const AGENCE_DOCUMENT_TYPE_LABELS: Record<AgenceDocumentType, string> = {
  contrat: "Contrat",
  "piece-identite": "Pièce d'identité",
  "document-bien": "Document du bien",
  recu: "Reçu",
  facture: "Facture",
  autre: "Autre",
};

export const AGENCE_DOCUMENT_TYPE_ICONS: Record<AgenceDocumentType, string> = {
  contrat: "ri-file-text-line",
  "piece-identite": "ri-id-card-line",
  "document-bien": "ri-home-4-line",
  recu: "ri-receipt-line",
  facture: "ri-file-list-3-line",
  autre: "ri-file-line",
};

export const AGENCE_DOCUMENT_TYPE_TONES: Record<
  AgenceDocumentType,
  "primary" | "accent" | "secondary" | "neutral"
> = {
  contrat: "primary",
  "piece-identite": "secondary",
  "document-bien": "accent",
  recu: "secondary",
  facture: "accent",
  autre: "neutral",
};

export const AGENCE_DOCUMENT_TYPE_OPTIONS = (
  Object.keys(AGENCE_DOCUMENT_TYPE_LABELS) as AgenceDocumentType[]
).map((value) => ({ value, label: AGENCE_DOCUMENT_TYPE_LABELS[value] }));

export function isAgenceImageDocument(doc: Pick<AgenceDocument, "fichierType">): boolean {
  return doc.fichierType.startsWith("image/");
}

export function isAgencePdfDocument(doc: Pick<AgenceDocument, "fichierType">): boolean {
  return doc.fichierType === "application/pdf";
}

export function formatAgenceFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/* ------------------------------------------------------------------ */
/* Synthèse propriétaire                                               */
/* ------------------------------------------------------------------ */

/**
 * Synthèse calculée pour la fiche d'un propriétaire :
 * immeubles, biens, locataires, contrats, loyers, paiements et impayés.
 */
export interface ProprietaireSummary {
  immeubles: AgenceImmeuble[];
  biens: AgenceBien[];
  locataires: AgenceLocataire[];
  contrats: AgenceContrat[];
  counts: {
    immeubles: number;
    biens: number;
    locataires: number;
    contrats: number;
  };
  loyers: { count: number; montantTotal: number };
  paiements: { count: number; montantTotal: number };
  impayes: { count: number; montantTotal: number };
}